import { Injectable, inject } from '@angular/core';
import {
  Observable,
  catchError,
  finalize,
  map,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { jwtDecode } from 'jwt-decode';
import { ImpersonationApiService } from '~shared/services/api/general/impersonation.api-service';
import {
  clearImpersonationSession,
  isImpersonationSession,
  readStoredToken,
  storeImpersonationToken,
} from '~shared/services/auth/auth-storage.util';
import { LocaleService } from '~shared/services/locale.service';
import { ModulesService } from '~shared/services/modules/modules.service';
import { mapMeModules } from '~shared/utils/me-modules.mapper';
import { CurrentUserService } from '~shared/services/current-user/current-user.service';

const APP_ROOT_URL = '/';

/** Claim `act` (RFC 8693) : l'utilisateur réel derrière la session. */
interface ActorClaim {
  uid: number;
  username?: string | null;
  full_name?: string | null;
}

interface ImpersonationTokenPayload {
  first_name?: string;
  last_name?: string;
  username?: string;
  act?: ActorClaim;
}

export interface ImpersonationInfo {
  targetName: string;
  actorName: string;
}

/**
 * Prise d'identité dans un onglet dédié : le jeton est reçu via un code à usage
 * unique (jamais dans l'URL), conservé dans le sessionStorage de l'onglet, et
 * la session de l'utilisateur réel n'est jamais touchée.
 */
@Injectable({ providedIn: 'root' })
export class ImpersonationSessionService {
  private readonly api = inject(ImpersonationApiService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly modules = inject(ModulesService);
  private readonly translate = inject(TranslateService);
  private readonly locale = inject(LocaleService);

  /**
   * Ouvre la session de la cible puis recharge tout ce qui dépend de
   * l'identité : profil (/me), menu et traductions. L'initialisation de
   * l'application a eu lieu sans jeton ; sans ce rechargement, le menu et le
   * tableau de bord restaient vides jusqu'à un F5.
   */
  start(handoffCode: string): Observable<void> {
    return this.api.redeem(handoffCode).pipe(
      tap(({ token }) => {
        storeImpersonationToken(token);
        this.currentUser.clear();
      }),
      switchMap(() => this.currentUser.reload()),
      tap((me) => {
        this.modules.setModules(mapMeModules(me));
        this.translate.reloadLang(this.locale.getLanguage()).subscribe();
      }),
      map(() => undefined),
      catchError((error: unknown) => {
        clearImpersonationSession();
        this.currentUser.clear();
        return throwError(() => error);
      })
    );
  }

  /** Informations du bandeau, ou null hors prise d'identité. */
  current(): ImpersonationInfo | null {
    const token = readStoredToken();
    if (!isImpersonationSession() || !token) {
      return null;
    }
    try {
      const payload = jwtDecode<ImpersonationTokenPayload>(token);
      if (!payload.act) {
        return null;
      }
      const targetName =
        [payload.first_name, payload.last_name].filter(Boolean).join(' ') ||
        payload.username ||
        '';
      return {
        targetName,
        actorName: payload.act.full_name || payload.act.username || '',
      };
    } catch {
      return null;
    }
  }

  /**
   * Trace la fin puis rend l'onglet à la session de l'utilisateur réel (ou à
   * l'écran de connexion s'il n'en a pas). La session locale est close même si
   * l'appel échoue (jeton déjà expiré).
   */
  end(): void {
    this.api
      .end()
      .pipe(
        finalize(() => {
          clearImpersonationSession();
          this.currentUser.clear();
          // Rechargement complet : menu, droits et profil redeviennent ceux de
          // l'utilisateur réel ; `replace` n'ajoute rien à l'historique.
          window.location.replace(APP_ROOT_URL);
        })
      )
      .subscribe({
        error: (error: unknown) =>
          console.warn("Fin de prise d'identité non tracée", error),
      });
  }
}
